import { useState, useEffect, useCallback } from 'react';
import { 
  Newspaper, ExternalLink, RefreshCw, Tractor, Scale, 
  Euro, TrendingUp, Wheat, ChevronRight, X, Filter
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icônes par catégorie
const CATEGORY_ICONS = {
  machines: Tractor,
  lois: Scale,
  aides: Euro,
  tendances: TrendingUp,
  marches: Euro,
  cultures: Wheat,
  elevage: Wheat,
  general: Newspaper,
  meteo: Newspaper,
};

// Couleurs par catégorie
const CATEGORY_COLORS = {
  machines: 'bg-blue-100 text-blue-800 border-blue-200',
  lois: 'bg-purple-100 text-purple-800 border-purple-200',
  aides: 'bg-green-100 text-green-800 border-green-200',
  tendances: 'bg-orange-100 text-orange-800 border-orange-200',
  marches: 'bg-amber-100 text-amber-800 border-amber-200',
  cultures: 'bg-lime-100 text-lime-800 border-lime-200',
  elevage: 'bg-pink-100 text-pink-800 border-pink-200',
  general: 'bg-gray-100 text-gray-800 border-gray-200',
  meteo: 'bg-sky-100 text-sky-800 border-sky-200',
};

// Composant carte d'actualité
const NewsCard = ({ item, compact = false }) => {
  const Icon = CATEGORY_ICONS[item.category] || Newspaper;
  const colorClass = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.general;
  
  const handleClick = () => {
    if (item.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    }
  };
  
  if (compact) {
    return (
      <button
        onClick={handleClick}
        disabled={!item.url}
        className={`w-full text-left p-2 rounded-lg hover:bg-muted/50 transition-colors group ${!item.url ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <div className="flex items-start gap-2">
          <div className={`p-1 rounded ${colorClass}`}>
            <Icon className="w-3 h-3" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium line-clamp-2 group-hover:text-primary">
              {item.title}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] text-muted-foreground">{item.source_name}</span>
              {item.url && <ExternalLink className="w-2.5 h-2.5 text-muted-foreground" />}
            </div>
          </div>
        </div>
      </button>
    );
  }
  
  return (
    <button
      onClick={handleClick}
      disabled={!item.url}
      className={`w-full text-left p-3 rounded-lg border hover:shadow-md transition-all group ${!item.url ? 'cursor-default' : 'cursor-pointer'}`}
    >
      <div className="flex items-start gap-3">
        {item.image_url && (
          <img 
            src={item.image_url} 
            alt="" 
            className="w-16 h-16 rounded object-cover flex-shrink-0"
            onError={(e) => e.target.style.display = 'none'}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${colorClass}`}>
              <Icon className="w-2.5 h-2.5 mr-1" />
              {item.category_label}
            </Badge>
            {item.source === 'ai_generated' && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-violet-50 text-violet-700">
                IA
              </Badge>
            )}
          </div>
          <h4 className="text-sm font-medium line-clamp-2 group-hover:text-primary mb-1">
            {item.title}
          </h4>
          {item.summary && (
            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
              {item.summary.replace(/<[^>]*>/g, '')}
            </p>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">{item.source_name}</span>
            {item.url && (
              <span className="text-[10px] text-primary flex items-center gap-1">
                Lire <ExternalLink className="w-2.5 h-2.5" />
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

// Widget icône minimale pour le dashboard
export const ActualitesWidget = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [newsCount, setNewsCount] = useState(0);
  
  // Charger juste le compte au démarrage
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_URL}/api/actualites/fil?limit=1`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setNewsCount(data.total || 0);
        }
      } catch (error) {
        console.error('Erreur:', error);
      }
    };
    fetchCount();
  }, []);
  
  return (
    <>
      {/* Icône minimale */}
      <button
        onClick={() => setShowDialog(true)}
        className="relative flex items-center justify-center w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 group"
        title="Fil d'actualités agricoles"
        data-testid="actualites-icon-btn"
      >
        <Newspaper className="w-5 h-5 text-green-600 group-hover:text-green-700" />
        {newsCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {newsCount > 9 ? '9+' : newsCount}
          </span>
        )}
      </button>
      
      {/* Dialog plein écran */}
      <ActualitesFullDialog 
        open={showDialog} 
        onClose={() => setShowDialog(false)}
      />
    </>
  );
};

// Dialog plein écran pour toutes les actualités
export const ActualitesFullDialog = ({ open, onClose }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  const fetchNews = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const categoryParam = category !== 'all' ? `&category=${category}` : '';
      const response = await fetch(
        `${API_URL}/api/actualites/fil?limit=50&include_ai=true&refresh=${refresh}${categoryParam}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setNews(data.items || []);
        if (data.categories) setCategories(data.categories);
      }
    } catch (error) {
      console.error('Erreur chargement actualités:', error);
    } finally {
      setLoading(false);
    }
  }, [category]);
  
  useEffect(() => {
    if (open) {
      fetchNews();
    }
  }, [open, fetchNews]);
  
  const filteredNews = category === 'all' 
    ? news 
    : news.filter(item => item.category === category);
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className={`${isFullScreen ? 'max-w-[98vw] max-h-[98vh] w-[98vw] h-[98vh]' : 'max-w-4xl max-h-[85vh]'} transition-all duration-300`}
      >
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-green-600" />
              Fil d'actualités agricoles
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsFullScreen(!isFullScreen)}
                title={isFullScreen ? "Réduire" : "Plein écran"}
              >
                {isFullScreen ? (
                  <X className="w-4 h-4" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex items-center gap-4 mb-4 flex-shrink-0">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[200px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Toutes catégories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {Object.entries(categories).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchNews(true)}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
          
          <span className="text-xs text-muted-foreground ml-auto">
            {filteredNews.length} actualités
          </span>
        </div>
        
        <ScrollArea className={`${isFullScreen ? 'h-[calc(98vh-140px)]' : 'h-[60vh]'} pr-4`}>
          {loading && news.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredNews.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Aucune actualité dans cette catégorie
            </p>
          ) : (
            <div className={`grid gap-3 ${isFullScreen ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
              {filteredNews.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Ancien composant conservé pour compatibilité
export const ActualitesDialog = ActualitesFullDialog;

export default ActualitesWidget;
